<script>
  import router from '../../../lib/router';
  import Radio from '../../../lib/components/Radio.svelte';
  import APIClient from '../../../state-charts/api-client';
  import { TS } from '../../../config/translation-keys.js';
  import { t9n } from '../../../lib/translate.js';
  import { show_message } from '../../../lib/flash';
  import AddCardDialog from '../../../cards/AddCardDialog.svelte';
  import session from '../../../lib/session';
  import { show_tutorial_screen } from '../../../lib/tutorials';
  import { onMount, tick } from 'svelte';

  // component options  ------------------------------------------------------ //

  export let items;
  export let category_selector;
  export let amount;
  export let has_runned;
  export let order;
  export let page_state;
  export let next_tab_disabled;
  export let validator;
  export let has_main_init = false;

  // translations variables ------------------------------------------------------ //

  let billing;
  let credit_card;
  let btn_prev;
  let btn_next;
  let notenoughpp;
  let btn_add_card;
  let payment_provider;
  let has_init = false;
  let items_revision = 0;

  // utility functions -------------------------------------------------------- //

  const get_orderer_price = () => {
    let products = $session.merchant.products;
    let tmp_price = 0;
    for (let i = 0; i < products.length; i++) {
      if (products[i].product_id === category_selector) {
        tmp_price = products[i].orderer_price;
      }
    }
    tmp_price *= amount;
    return tmp_price;
  };

  const normalize_expiry_year = year => {
    let y = Number(year);
    if (Number.isNaN(y)) return null;
    if (y < 100) y += 2000;
    return y;
  };

  const is_card_expired = (month, year) => {
    const y = normalize_expiry_year(year);
    const m = Number(month);
    if (!y || Number.isNaN(m)) return false;
    const now = new Date();
    return (
      y < now.getFullYear() ||
      (y === now.getFullYear() && m < now.getMonth() + 1)
    );
  };

  const load_cards = async () => {
    validator = true;
    let price = get_orderer_price();

    await APIClient.get_payment_options_with_succ({ price: price }, result => {
      const next_items = [];

      if (result.options.length > 0) {
        for (let i = 0; i < result.options.length; i++) {
          if (result.options[i].payment_type === 'payment_type_credit_card') {
            const card = result.options[i].data.credit_card;
            const expired = is_card_expired(card.expiry_month, card.expiry_year);
            next_items.push({
              name:
                credit_card +
                ' ' +
                card.credit_card_number,
              is_enabled: !expired,
              payment_type: 'payment_type_credit_card',
              credit_card_id: card.credit_card_id,
              expiry_month: card.expiry_month,
              expiry_year: card.expiry_year,
              is_selected: false,
            });
          }
          if (result.options[i].payment_type === 'payment_type_billing') {
            next_items.push({
              name: billing,
              is_enabled: true,
              payment_type: 'payment_type_billing',
              is_selected: false,
            });
          }
          if (result.options[i].payment_type === 'payment_type_pilot_points') {
            next_items.push({
              name: 'PilotPoints (' + $session.user.stats.pilot_points + ')',
              is_enabled: true,
              payment_type: 'payment_type_pilot_points',
              is_selected: false,
            });
          }
        }

        const first_enabled = next_items.findIndex(item => item.is_enabled);
        if (first_enabled !== -1) {
          next_items[first_enabled].is_selected = true;
        }
      } else {
        next_items.push({
          name: 'PilotPoints (' + $session.user.stats.pilot_points + ')',
          is_enabled: false,
          payment_type: 'payment_type_pilot_points',
          is_selected: true,
        });
        if (!JSON.parse(window.localStorage.getItem('WP.tutorial.new_order'))) {
          show_message(notenoughpp, 'info');
          validator = false;
        }
      }

      items = next_items;
      items_revision += 1;
    });
    await tick();
  };

  const credit_card_fingerprint = list =>
    (list || [])
      .filter(i => i.payment_type === 'payment_type_credit_card')
      .map(i => i.credit_card_id)
      .sort()
      .join(',');

  // Nets iframe OK can arrive before the server callback finishes registering
  // the card, so retry until payment options actually change.
  const add_card = async () => {
    const before = credit_card_fingerprint(items);

    for (let attempt = 0; attempt < 20; attempt++) {
      await load_cards();
      if (credit_card_fingerprint(items) !== before) {
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const open_add_card = () => {
    let target = document.getElementById('card-dialog');
    target.innerHTML = ''; //todo: check if exists!
    let dialog = new AddCardDialog({
      target: target,
      props: { config: $session, add_card: add_card },
    });
    dialog.open();
  };

  // reactive functions ------------------------------------------------------- //

  const translate_texts = () => {
    credit_card = t9n(TS.NEW_ORDER_PAY_CARD);
    btn_prev = t9n(TS.TABLE_BUTTON_PREV);
    btn_next = t9n(TS.TABLE_BUTTON_NEXT);
    billing = t9n(TS.NEW_ORDER_WP_BILLING);
    notenoughpp = t9n(TS.NEW_ORDER_FORM_PAYMENT_NO_PAYMENT_SETUP);
    btn_add_card = t9n(TS.CARDS_ADD_CARD_LABEL);
  };

  $: if (JSON.parse(window.localStorage.getItem('WP.tutorial.new_order'))) {
    show_tutorial_screen(true, 'new_order_o12', 200, 'orderer');
  }

  $: if (!!$session) {
    translate_texts();
    payment_provider = $session.merchant.payment_provider;
  }

  onMount(async () => {
    if (page_state !== 'edit' && page_state !== 'copy') {
      await load_cards();
    }
  })
</script>

<div id="card-dialog" />

<div class="fade-in">
  {#key items_revision}
    <Radio bind:items />
  {/key}

  <p />

  <div style="display: flex; justify-content: flex-end; margin-top: 50px">
    <div style="margin-right: auto;">
      {#if payment_provider !== "None"}
      <button
        on:click={() => open_add_card()}
        class="mdc-button mdc-button--unelevated margin-5">
        <span class="mdc-button__ripple" />
        {btn_add_card}
      </button>
      {/if}
    </div>
    <div style="margin-right: 10px;">
      <button
        onclick="prev_tab()"
        class="mdc-button mdc-button--outlined margin-5">
        <span class="mdc-button__ripple" />
        {btn_prev}
      </button>
    </div>


      <button
        onclick="next_tab()"
        disabled={next_tab_disabled} class="mdc-button mdc-button--unelevated margin-5">
        <span class="mdc-button__ripple" />
        {btn_next}
      </button>
  </div>

</div>

<style>
  .setup-card {
    position: relative;
    top: -54px;
    left: 100px;
  }
</style>
